import { BreakpointObserver, Breakpoints, BreakpointState, LayoutModule } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { combineLatest, Observable, Subscription, take } from 'rxjs';
import { IframeDialogComponent } from 'src/app/components/iframe-dialog/iframe-dialog.component';
import { User } from 'src/app/modules/auth/entities/user.entity';
import { AuthenticationService } from 'src/app/modules/auth/services/authentication.service';
import { environment } from 'src/environments/environment';
import { FeatureFlagDirective } from '~modules/administration/directives/feature-flags.directive';
import { MixpanelService } from '~modules/analytics/mixpanel.service';
import { CompanyModule } from '~modules/company/company.module';
import { CurrentCompanyService } from '~modules/company/services/current-company.service';
import { ConnectorsModule } from '~modules/connectors/connectors.module';

@Component({
  selector: 'app-logged-layout',
  templateUrl: './logged-layout.component.html',
  styleUrls: ['./logged-layout.component.scss'],
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    MatButtonModule,
    LayoutModule,
    RouterModule,
    CommonModule,
    ConnectorsModule,
    CompanyModule,
    FeatureFlagDirective
  ]
})
export class LoggedLayoutComponent implements OnInit, OnDestroy {

  user$!: Observable<User|null>;
  currentApplicationVersion: string = environment.appVersion;
  storyToDocUrl = environment.storyToDocUrl;

  updateUserConnectionsSubscription?: Subscription;

  currentScreenSize!: BreakpointState;

  @ViewChild(MatDrawer)
  drawer!: MatDrawer;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly breakpoint: BreakpointObserver,
    private readonly destroyRef: DestroyRef,
    private readonly mixPanelService: MixpanelService,
    private readonly currentCompanyService: CurrentCompanyService,
    private readonly dialog: MatDialog
  ) {}

  manageSideNavOpeningWithScreenSize(breakpoint: BreakpointObserver): void {
    breakpoint
    .observe([
      Breakpoints.XSmall,
    ])
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
      if (result.matches === true) {
        this.drawer?.close();
      }
    });

    breakpoint
      .observe([
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result.matches === true) {
          this.drawer?.open();
        }
      });
  }

  ngOnInit(): void {
    combineLatest([this.authService.currentUser$,
      this.currentCompanyService.current$]).pipe(take(1)).subscribe(([user, company]) => {
        if (user) {
          this.mixPanelService.identify(user.id.toString());
          this.mixPanelService.setUserProperties({
          email: user.email,
          name: `${user.lastName} ${user.firstName}`,
          companyId: company.id,
          companyName: company.name
          });
        }
      });

    this.updateUserConnectionsSubscription = this.authService.updateUserConnections$().subscribe();
    this.manageSideNavOpeningWithScreenSize(this.breakpoint);
  }

  openDialog(): void {
    this.dialog.open(IframeDialogComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '100vw',
      panelClass: 'iframe-dialog-panel',
      data: { url: this.storyToDocUrl }
    });
  }

  ngOnDestroy() {
    this.mixPanelService.reset();
    this.updateUserConnectionsSubscription?.unsubscribe();
  }

  signOut(): void {
    this.authService.signOut();
  }
}
